// Technical-support entitlement.
//
// A user is entitled to support for a set of products, drawn from two sources
// that an admin sets manually:
//   • userData.supportProducts   — comma-separated products on the user doc
//   • organization.supportProducts — comma-separated products on their approved org
// The effective entitlement is the UNION of both, normalized.
//
// "*" (or "all") entitles every product — used for Enterprise/Priority accounts.

import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/config";

const norm = (s) => String(s || "").trim().toLowerCase();

export const parseProducts = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(norm).filter(Boolean);
  return String(value)
    .split(",")
    .map(norm)
    .filter(Boolean);
};

// Canonical product display names — used to show a clean list in the UI/Sentry
export const SUPPORT_PRODUCTS = [
  "Vitraux", "EchoMarks", "DocuLens", "MetricSight",
  "NeuralOps", "DataSynapse", "SecureAI Vault", "IntelliFlow",
  "CustomerAI", "SupplyGuard", "MarketSense", "TalentNexus",
  "Neural SDK", "AI Sandbox", "ModelForge", "API Gateway",
];

const displayName = (slug) =>
  SUPPORT_PRODUCTS.find((p) => norm(p) === norm(slug)) || slug;

class SupportService {
  // Find the first approved org the user belongs to (support follows the org)
  async _findUserOrg(uid) {
    if (!uid) return null;
    try {
      // members is an array of maps; query by the denormalized memberUids array
      const snap = await getDocs(
        query(collection(db, "organizations"), where("memberUids", "array-contains", uid))
      );
      for (const d of snap.docs) {
        const data = d.data();
        const me = (data.members || []).find((m) => m.uid === uid && m.status === "approved");
        if (me) return { id: d.id, ...data };
      }
    } catch {
      // memberUids may not exist on older orgs — fall back to a full read is
      // avoided on purpose (would need admin rules); user-level tags still work.
    }
    return null;
  }

  /**
   * Resolve entitlement for the signed-in user.
   * @returns {{ all: boolean, products: string[], source: 'none'|'user'|'org'|'both' }}
   */
  async getEntitlement(userData) {
    if (!userData) return { all: false, products: [], source: "none" };

    const userTags = parseProducts(userData.supportProducts);
    let orgTags = [];
    const org = await this._findUserOrg(userData.uid || userData.id);
    if (org) orgTags = parseProducts(org.supportProducts);

    const all = [...userTags, ...orgTags].some((t) => t === "*" || t === "all");
    if (all) {
      return { all: true, products: SUPPORT_PRODUCTS.slice(), source: userTags.length && orgTags.length ? "both" : userTags.length ? "user" : "org" };
    }

    const merged = Array.from(new Set([...userTags, ...orgTags])).map(displayName);
    const source =
      userTags.length && orgTags.length ? "both" : userTags.length ? "user" : orgTags.length ? "org" : "none";
    return { all: false, products: merged, source };
  }

  // Compact string for the chat payload — "*" or "Vitraux,NeuralOps"
  entitlementToken(entitlement) {
    if (!entitlement) return "";
    if (entitlement.all) return "*";
    return entitlement.products.join(",");
  }
}

const supportService = new SupportService();
export default supportService;
